import { useDescope } from '@descope/react-sdk';
import {
	AccountCircle,
	LogoutOutlined,
	SwitchAccountOutlined
} from '@mui/icons-material';
import {
	Avatar,
	CircularProgress,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Stack,
	Typography
} from '@mui/material';
import React, { useRef } from 'react';
import { useThemeCtx } from 'state/theme/ThemeCtx';
import ThemeModeButton from 'state/theme/ThemeSwitch';
import { useHasMultipleTenants, useLoggedInUser } from 'state/user';
import { useErrorCtx } from 'state/error/ErrorCtx';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'app/Views/routes';
import IconMenu, { IconMenuHandle } from './IconMenu';

const UserMenu: React.FC = () => {
	const iconMenuRef = useRef<IconMenuHandle>(null);
	const { user, isUserLoading } = useLoggedInUser();
	const hasMultipleTenants = useHasMultipleTenants();
	const { logout } = useDescope();
	const { toggleMode } = useThemeCtx();
	const { setError } = useErrorCtx();
	const navigate = useNavigate();

	const displayName = user?.name || user?.email || user?.loginIds?.[0];

	return (
		<IconMenu
			ref={iconMenuRef}
			icon={
				<Avatar src={user?.picture} sx={{ width: 28, height: 28 }}>
					<AccountCircle />
				</Avatar>
			}
			id="user-menu"
			buttonTestId="user-menu-button"
			menuProps={{
				slotProps: {
					paper: {
						sx: { minWidth: 220 }
					}
				}
			}}
		>
			{user && (
				<MenuItem disabled sx={{ py: 1.5 }}>
					<Stack direction="row" alignItems="center" gap={1}>
						{isUserLoading ? (
							<CircularProgress size={20} />
						) : (
							<>
								<Avatar src={user?.picture} sx={{ width: 24, height: 24 }}>
									<AccountCircle color="action" fontSize="small" />
								</Avatar>
								<Typography variant="body2" color="text.secondary">
									{displayName}
								</Typography>
							</>
						)}
					</Stack>
				</MenuItem>
			)}
			<MenuItem
				onClick={(e) => {
					e.stopPropagation();
					toggleMode();
				}}
			>
				<ListItemText primary={intl.tr('general.theme')} />
				<ThemeModeButton />
			</MenuItem>
			{user && hasMultipleTenants && (
				<MenuItem
					onClick={() => {
						iconMenuRef.current?.close();
						navigate(ROUTES.tenantSelect);
					}}
					data-testid="user-menu-switch-tenant"
				>
					<ListItemIcon>
						<SwitchAccountOutlined fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={intl.tr('general.switchOrganization')}
						slotProps={{ primary: { variant: 'body2' } }}
					/>
				</MenuItem>
			)}
			{user && (
				<MenuItem
					onClick={async () => {
						iconMenuRef.current?.close();
						await logout();
						setError(null);
					}}
					data-testid="user-menu-logout"
				>
					<ListItemIcon>
						<LogoutOutlined fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={intl.tr('general.logout')}
						slotProps={{ primary: { variant: 'body2' } }}
					/>
				</MenuItem>
			)}
		</IconMenu>
	);
};

export default UserMenu;
